import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Stack, Alert, Group, Text, Box, ScrollArea, Checkbox, TextInput, Loader, Center } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { z } from "zod";
import { IconAlertCircle, IconSignature } from "@tabler/icons-react";

import { useGetContractPreview } from "@hooks/queries/onboarding/useGetContractPreview";
import { useSignContract } from "@hooks/mutations/onboarding/useSignContract";
import { useUpdateMySession } from "@hooks/mutations/onboarding/useUpdateMySession";
import { useAuth } from "@contexts/auth";

const schema = z.object({
  agreed: z.boolean().refine(val => val === true, {
    message: "You must agree to the terms to proceed",
  }),
  signatureName: z.string().min(2, "Please type your full legal name to sign"),
});

type Inputs = z.infer<typeof schema>;

interface Step6Props {
  sessionData: any;
}

export const Step7Contract = ({ sessionData }: Step6Props) => {
  const { t } = useTranslation();
  const { setOnboardingStatus } = useAuth();
  const { data: contractHtml, isLoading: isPreviewLoading, isError } = useGetContractPreview();
  const { mutate: signContract, isLoading: isSigning } = useSignContract();
  const { mutate: updateSession, isLoading: isUpdating } = useUpdateMySession();

  const form = useForm<Inputs>({
    mode: "uncontrolled",
    validate: zodResolver(schema),
    initialValues: {
      agreed: false,
      signatureName: "",
    },
  });

  const handleSignAndSubmit = (values: Inputs) => {
    signContract(
      { signature_name: values.signatureName },
      {
        onSuccess: () => {
          // Instantly update the auth context to "pending_review" so the UI immediately drops into the locked screen
          setOnboardingStatus("pending_review");
        }
      }
    );
  };

  const handleBack = () => {
    updateSession({ current_step: 6 });
  };

  if (isPreviewLoading) {
    return (
      <Center p="xl">
        <Stack align="center">
          <Loader />
          <Text c="dimmed">{t("onboarding-generating-contract")}</Text>
        </Stack>
      </Center>
    );
  }

  if (isError || !contractHtml) {
    return (
      <Alert color="red" title="Error">
        Failed to load contract preview. Please try refreshing the page.
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      <Alert color="blue" icon={<IconAlertCircle size={16} />}>
        {t("onboarding-review-agreement")}
      </Alert>

      <Box 
        style={{ 
          border: "1px solid #e9ecef", 
          borderRadius: "8px", 
          background: "white",
          overflow: "hidden"
        }}
      >
        <ScrollArea h={500} p="xl" style={{ backgroundColor: "#f8f9fa" }}>
          <Box 
            style={{ 
              background: "white", 
              padding: "40px", 
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              margin: "0 auto",
              maxWidth: "800px"
            }}
            dangerouslySetInnerHTML={{ __html: contractHtml }}
          />
        </ScrollArea>
      </Box>

      <form onSubmit={form.onSubmit(handleSignAndSubmit)}>
        <Stack gap="md" mt="md" p="md" style={{ background: "#f8f9fa", borderRadius: "8px" }}>
          <Checkbox
            label={t("onboarding-read-agree")}
            size="md"
            key={form.key("agreed")}
            {...form.getInputProps("agreed", { type: "checkbox" })}
            error={form.errors.agreed as string}
          />
          
          <Box mt="sm">
            <Text fw={500} size="sm" mb="xs">{t("onboarding-e-signature")} <Text span c="red">*</Text></Text>
            <Text size="sm" c="dimmed" mb="sm">
              {t("onboarding-sign-desc")}
            </Text>
            <TextInput
              placeholder="e.g. John Doe"
              size="xl"
              leftSection={<IconSignature size={24} />}
              className="signature-input"
              key={form.key("signatureName")}
              {...form.getInputProps("signatureName")}
            />
          </Box>

          <Group justify="space-between" mt="md">
            <Button variant="outline" onClick={handleBack} loading={isUpdating} type="button">
              {t("onboarding-back")}
            </Button>
            <Button 
              type="submit"
              size="lg" 
              loading={isSigning}
            >
              {t("onboarding-sign-submit")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Stack>
  );
};
